// MonthView.jsx

import React, { useLayoutEffect, useMemo, useRef } from 'react';

// Context
import { useCalendarTimeline } from 'context/CalendarTimelineContext';

// Utilities
import { normalizeDateValue } from 'utilities/DateTime';
import { groupEventsByDateKey } from 'views/calendar/shared/calendarUtils';

// Hooks
import useMonthGridDays from './hooks/useMonthGridDays';
import useCalendarDateFields from 'views/calendar/hooks/useCalendarDateFields';
import useCalendarEvents from 'views/calendar/hooks/useCalendarEvents';

// CSS
import './MonthView.css';

// Components
import MonthViewDayCell from './day/MonthViewDayCell';
import MonthViewHeader from './header/MonthViewHeader';
import CalendarNavControls from 'views/calendar/shared/CalendarNavControls';

/**
 * MonthView
 * 
 * Renders a month view.
 * 
 * @param {Object} view - View configuration.
 * @param {Object} typeDef - Type definition for events.
 * @param {Array} nodes - Event nodes to display.
 * @param {string} language - Language code for localization (default: 'en').
 *  
 *
 * @returns {ReactNode} - MonthView.
 */
const MonthView = React.memo(({
    view,
    typeDef,
    nodes = [],
    eventItems,
    language = 'en',
    onNodeClick,
    onNodeDoubleClick,
    onAddDate,
    canDragEvents = false,
    draggingItem = null,
    dragOverTargetKey = null,
    onEventDragStart,
    onEventDragEnd,
    onDropTargetDragOver,
    onMoveToTarget,
}) => {

    // ----- CONTEXT ----- //

    // Global Context

    const { selectedDate, setSelectedDate } = useCalendarTimeline();

    // ----- REFS ----- //

    const containerRef = useRef(null);
    const gridRef = useRef(null);

    // ----- HOOKS ----- //

    const { typeKey, dateFields } = useCalendarDateFields(typeDef, { preferredFieldKey: view?.calendarField });

    // ----- MEMOS ----- //

    // Base date for the month view (normalized selected date or current date)

    const baseDate = useMemo(
        () => normalizeDateValue(selectedDate) || new Date(),
        [selectedDate]
    );

    // Today's date (used for highlighting current day)
    const today = useMemo(() => new Date(), []);

    // Title for the month view (e.g., "September 2024")
    const monthTitle = useMemo(() => (
        baseDate.toLocaleDateString(language || 'en', {
            month: 'long',
            year: 'numeric',
        })
    ), [baseDate, language]);

    // ----- HOOKS ----- //

    // Generate the grid of days for the month view

    const monthDays = useMonthGridDays({ baseDate, today });

    const rangeStart = monthDays[0]?.date || null;
    const rangeEnd = monthDays[monthDays.length - 1]?.date || null;

    // Fetch and process events for the month view
    const resolvedEventItems = useCalendarEvents({ nodes, dateFields, typeKey, eventItems, rangeStart, rangeEnd });

    // ----- MEMOS ----- //

    // Group events by date key for efficient lookup when rendering day cells

    const eventsByDate = useMemo(
        () => groupEventsByDateKey(resolvedEventItems),
        [resolvedEventItems]
    );

    // ----- HANDLERS ----- //

    /**
     * Handles month navigation.
     * 
     * @param {number} direction - Direction to navigate (-1 for previous, 1 for next).
     */

    const handleMonthChange = (direction) => {
        const nextMonth = new Date(
            baseDate.getFullYear(),
            baseDate.getMonth() + direction,
            1
        );
        setSelectedDate(nextMonth);
    };

    // ----- EFFECTS ----- //

    useLayoutEffect(() => {
        const container = containerRef.current;
        const grid = gridRef.current;
        const scrollParent = container?.parentElement;
        if (!container || !grid || !scrollParent) return undefined;

        const syncRowMinHeight = () => {
            const availableHeight = scrollParent.clientHeight - grid.offsetTop;
            const rowMinHeight = Math.max(120, Math.floor(availableHeight / 6));
            container.style.setProperty('--month-row-min-height', `${rowMinHeight}px`);
        };

        syncRowMinHeight();

        const resizeObserver = new ResizeObserver(syncRowMinHeight);
        resizeObserver.observe(scrollParent);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, []);

    // ----- RENDER ----- //

    return (

        <div
          className="month-view-container"
          ref={containerRef}
        >

            <CalendarNavControls
              title={monthTitle}
              prevAriaLabel="Previous month"
              nextAriaLabel="Next month"
              onNext={() => handleMonthChange(1)}
              onPrev={() => handleMonthChange(-1)}
              onToday={() => setSelectedDate(new Date())}
            />

            <MonthViewHeader />

            {/* GRID */}
            <div
              className="month-view-grid"
              ref={gridRef}
            >
                {monthDays.map((day) => {
                    const items = eventsByDate.get(day.key) || [];
                    return (
                        <MonthViewDayCell
                          key={day.key}
                          day={day}
                          items={items}
                          language={language}
                          canDragEvents={canDragEvents}
                          draggingItem={draggingItem}
                          dragOverTargetKey={dragOverTargetKey}
                          onAddDate={onAddDate}
                          onDropTargetDragOver={onDropTargetDragOver}
                          onEventDragEnd={onEventDragEnd}
                          onEventDragStart={onEventDragStart}
                          onMoveToTarget={onMoveToTarget}
                          onNodeClick={onNodeClick}
                          onNodeDoubleClick={onNodeDoubleClick}
                        />
                    );
                })}
            </div>
        </div>

    );

});

// Dev Tools
MonthView.displayName = 'MonthView';

export default MonthView;
